import { Star, ExternalLink, Quote } from "lucide-react";
import { REVIEWS, GOOGLE_REVIEW_URL } from "@/lib/constants";
import { ChapterLabel, MaskReveal, Reveal } from "@/components/Reveal";

function Stars() {
  return (
    <span className="flex items-center gap-1" aria-label="5 out of 5 stars">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className="h-3.5 w-3.5 fill-[#B08D2E] text-[#B08D2E]" strokeWidth={1} />
      ))}
    </span>
  );
}

export default function Reviews() {
  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative bg-[#F4EFE4] py-28 sm:py-36"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(168,197,229,0.35),transparent_55%)]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <ChapterLabel index="04" title="What Our Customers Say" testId="chapter-reviews" />
            <h2 className="mt-8 max-w-2xl font-display text-4xl font-light leading-[1.08] tracking-tight text-[#1A1A1A] sm:text-5xl lg:text-6xl">
              <MaskReveal>Trusted by the</MaskReveal>
              <MaskReveal delay={0.12}>
                <em className="gold-text font-medium italic">neighbourhood.</em>
              </MaskReveal>
            </h2>
          </div>
          <Reveal delay={0.2}>
            <div className="flex flex-col items-start gap-3">
              <Stars />
              <p className="max-w-xs text-sm font-light leading-relaxed tracking-wide text-[#6E675C]">
                Five-star care, praised by customers across Yiewsley, West
                Drayton and beyond.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="mt-20 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {REVIEWS.map((review, i) => (
            <Reveal key={review.name} delay={0.08 * (i % 4)} className="h-full">
              <article
                data-testid={`review-card-${i + 1}`}
                className="group relative flex h-full flex-col border border-[#B08D2E]/20 bg-[#FFFFFF] p-8 shadow-[0_4px_24px_rgba(26,26,26,0.04)] transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-[#B08D2E]/55 hover:shadow-[0_20px_50px_rgba(176,141,46,0.12)]"
              >
                <Quote
                  className="absolute right-6 top-6 h-7 w-7 text-[#B08D2E]/15 transition-colors duration-500 group-hover:text-[#B08D2E]/35"
                  strokeWidth={1}
                />
                <Stars />
                <p className="mt-6 font-display text-lg font-light italic leading-relaxed text-[#1A1A1A]">
                  “{review.text}”
                </p>
                <div className="mt-auto pt-8">
                  <span className="block h-px w-10 bg-[#B08D2E]/30 transition-[width,background-color] duration-500 group-hover:w-full group-hover:bg-[#B08D2E]/60" />
                  <p className="mt-5 text-sm font-medium tracking-wide text-[#1A1A1A]">
                    {review.name}
                  </p>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-[#6E675C]">
                    {review.area} · Google Review
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15} className="mt-16">
          <div className="flex flex-col items-start gap-6 border border-[#B08D2E]/15 bg-[#FFFFFF] p-8 shadow-[0_4px_24px_rgba(26,26,26,0.04)] sm:flex-row sm:items-center sm:justify-between sm:p-10">
            <p className="max-w-md font-display text-2xl font-light italic text-[#1A1A1A]">
              Enjoyed our care?{" "}
              <span className="gold-text font-medium">Tell the neighbourhood.</span>
            </p>
            <a
              data-testid="google-review-btn"
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex shrink-0 items-center gap-3 overflow-hidden bg-[#B08D2E] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#F4EFE4] transition-colors duration-300 hover:bg-[#96771F]"
            >
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />
              <Star className="h-4 w-4 fill-current" strokeWidth={1.5} />
              Review Us on Google
              <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
